import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { NSFavoritesConstants } from '@ruby/configs/favorites.constants';
import { ICRActiveReportsConfig, ICRFavoriteReport } from '@ruby/modules/customized-reporting/models/report.interface';
import { ReportDetailsContainer } from '@ruby/shared/dialogs/customized-reporting/report-details/containers/report-details/report-details.container';
import { ReportViewContainer } from '@ruby/shared/dialogs/customized-reporting/report-view/containers/report-view/report-view.container';
import { ECRFormat } from '@ruby/shared/enums/customized-reporting.enums';

@Component({
  selector: 'ng-favorites-report',
  templateUrl: './favorites-report.component.html',
  styleUrls: ['./favorites-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoritesReportComponent {
  @Input() favoriteReports!: Array<ICRFavoriteReport>;
  @Input() config?: ICRActiveReportsConfig;
  @Output() runReport = new EventEmitter<number>();
  @Output() downloadReport = new EventEmitter<{ id: number; name: string }>();
  @Output() addFavorite = new EventEmitter<number>();
  @Output() removeFavorite = new EventEmitter<number>();
  @Output() deleteReport = new EventEmitter<number>();
  @Output() selectFavorite = new EventEmitter<ICRFavoriteReport>();
  htmlFormat = ECRFormat.HTML;
  allowedScreens: Array<string> = NSFavoritesConstants.allowedScreens;

  constructor(private dialogSvc: MatDialog) { }

  get today(): number { return new Date().getTime(); }

  /**
   * Open Details
   *
   * @summary Opens the report details dialog
   * @param id: number
   * @returns void
   */
  openDetails(id: number): void {
    this.dialogSvc.open(ReportDetailsContainer, {
      width: '70%',
      height: '80%',
      data: id
    });
  }

  /**
   * View
   *
   * @summary View the report
   * @param $event: Event
   * @param id: number
   * @returns void
   */
  view($event: Event, id: number): void {
    $event.stopPropagation();
    this.dialogSvc.open(ReportViewContainer, {
      width: '50%',
      height: '80%',
      data: id.toString(10)
    });
  }

  /**
   * Run
   *
   * @summary Run the report
   * @param $event: Event
   * @param id: number
   * @returns void
   */
  run($event: Event, id: number): void {
    $event.stopPropagation();
    this.runReport.emit(id);
  }

  /**
   * Download
   *
   * @summary Download the report
   * @param $event: Event
   * @param id: number
   * @param name: string
   * @returns void
   */
  download($event: Event, id: number, name: string): void {
    $event.stopPropagation();
    this.downloadReport.emit({ id, name });
  }

  /**
   * Manage Favorites
   *
   * @summary Removes the favorite or add the favorite otherwise
   * @param $event: Event
   * @param fav: ICRFavoriteReport
   * @returns void
   */
  manageFavorite($event: Event, fav: ICRFavoriteReport): void {
    $event.stopPropagation();
    fav.favorite ? this.removeFavorite.emit(fav.id) : this.addFavorite.emit(fav.id);
  }

  /**
   * Select Report
   *
   * @summary Method to load or navigate to the report screen
   * @param $event: Event
   * @param $fav: ICRFavoriteReport
   * @returns void
   */
  selectReport($event: Event, $fav: ICRFavoriteReport): void {
    $event.stopPropagation();
    if (!this.config?.disable?.rowClick && this.allowedScreens.includes($fav.screenName)) {
      this.selectFavorite.emit($fav);
    }
  }

  /***
   * Delete
   *
   * @summary Method to delete a favorite
   * @param $event: Event
   * @param $id: number
   * @returns void
   */
  delete($event: Event, $id: number): void {
    $event.stopPropagation();
    this.deleteReport.emit($id);
  }
}
